'use client'

import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { TabNavigation } from '../_components/tab-navigation'
import { useMyPageTabs } from '../_hooks/tab-navigation/useMyPageTabs'
import { Input } from '@/components/ui/input'
import { useForm } from 'react-hook-form'
import { useUpdateProfileMutation, useCurrentUserQuery } from '@/api/index'
import { UpdateProfileValues } from '../_schema/type'
import { updateProfileSchema } from '../_schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useToast } from '@/hooks/use-toast'
import { useIsMounted } from '@/hooks/use-is-mounted'

export default function Profile() {
  const { toast } = useToast()
  const isMounted = useIsMounted()
  const { tabs, activeTab, subTabs, activeSubTab } = useMyPageTabs()

  const { data: { currentUser } = {} } = useCurrentUserQuery()
  const [updateProfile] = useUpdateProfileMutation({
    onCompleted: (data) => {
      if (!data.updateProfile?.error) {
        toast({ title: 'プロフィールの更新が完了しました' })
      } else {
        toast({
          title: 'プロフィールの更新に失敗しました',
          variant: 'destructive',
        })
      }
    },
    onError: () => {
      toast({
        title: '更新中にエラーが発生しました。もう一度お試しください。',
        variant: 'destructive',
      })
    },
  })

  const form = useForm<UpdateProfileValues>({
    resolver: zodResolver(updateProfileSchema),
    mode: 'all',
    defaultValues: {
      lastName: currentUser?.lastName || '',
      firstName: currentUser?.firstName || '',
      lastNameKana: currentUser?.lastNameKana || '',
      firstNameKana: currentUser?.firstNameKana || '',
      tel: currentUser?.tel || '',
    },
  })

  useEffect(() => {
    if (currentUser) {
      form.reset({
        lastName: currentUser.lastName || '',
        firstName: currentUser.firstName || '',
        lastNameKana: currentUser.lastNameKana || '',
        firstNameKana: currentUser.firstNameKana || '',
        tel: currentUser.tel || '',
      })
    }
  }, [currentUser, form])

  const onSubmit = (data: UpdateProfileValues) => {
    if (!isMounted) return

    updateProfile({
      variables: {
        input: {
          input: {
            lastName: data.lastName,
            firstName: data.firstName,
            lastNameKana: data.lastNameKana,
            firstNameKana: data.firstNameKana,
            tel: data.tel,
          },
        },
      },
    })
  }

  if (!isMounted) {
    return null
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="mb-[18px] md:mb-10 mt-12 md:mt-20">
        <h1 className="text-2xl md:text-[32px] font-heading text-center tracking-wide">
          マイページ
        </h1>
      </div>
      <div className="mb-32">
        <div className="mb-8 md:mb-16">
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            subTabs={subTabs}
            activeSubTab={activeSubTab}
          />
        </div>
        <div className="flex justify-center">
          <div className="w-[327px] sm:w-[100%] md:w-[736px]">
            <div className="text-center">
              <div className="text-[20px] md:text-2xl font-base">個人情報</div>
              <div className="mt-4 text-sm">
                変更したいところの情報を修正して、変更の保存ボタンを押してください。
              </div>
            </div>
            <div className="flex flex-col gap-4 mt-6 md:mt-10 px-0 lg:px-[45px]">
              <div>
                <span className="inline-block mb-2 text-sm font-bold">
                  お名前<span className="text-[#FF0000]">*</span>
                </span>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="w-full">
                    <Input
                      type="text"
                      placeholder="姓"
                      {...form.register('lastName')}
                    />
                    {form.formState.errors.lastName && (
                      <span className="text-xs text-[#FF0000]">
                        {form.formState.errors.lastName.message}
                      </span>
                    )}
                  </div>
                  <div className="w-full">
                    <Input
                      type="text"
                      placeholder="名"
                      {...form.register('firstName')}
                    />
                    {form.formState.errors.firstName && (
                      <span className="text-xs text-[#FF0000]">
                        {form.formState.errors.firstName.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4 mt-2 md:mt-4 px-0 lg:px-[45px]">
              <div>
                <span className="inline-block mb-2 text-sm font-bold">
                  フリガナ<span className="text-[#FF0000]">*</span>
                </span>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="w-full">
                    <Input
                      type="text"
                      placeholder="フリガナ(姓)"
                      {...form.register('lastNameKana')}
                    />
                    {form.formState.errors.lastNameKana && (
                      <span className="text-xs text-[#FF0000]">
                        {form.formState.errors.lastNameKana.message}
                      </span>
                    )}
                  </div>
                  <div className="w-full">
                    <Input
                      type="text"
                      placeholder="フリガナ(名)"
                      {...form.register('firstNameKana')}
                    />
                    {form.formState.errors.firstNameKana && (
                      <span className="text-xs text-[#FF0000]">
                        {form.formState.errors.firstNameKana.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 mt-2 md:mt-4 px-0 lg:px-[45px]">
              <div>
                <span className="inline-block mb-2 text-sm font-bold">
                  電話番号<span className="text-[#FF0000]">*</span>
                </span>
                <div className="w-full">
                  <Input
                    type="tel"
                    placeholder="09012345678"
                    {...form.register('tel')}
                  />
                  {form.formState.errors.tel && (
                    <span className="text-xs text-[#FF0000]">
                      {form.formState.errors.tel.message}
                    </span>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    ハイフンなしで入力してください
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-8 sm:mt-12">
              <Button
                type="submit"
                className="h-12 w-[327px] bg-black text-sm font-heading sm:h-14 sm:w-[346px] rounded-none p-4"
                disabled={
                  !form.formState.isValid || form.formState.isSubmitting
                }
              >
                {form.formState.isSubmitting ? '更新中...' : '変更の保存'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  )
}
